import { useState } from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import { OutlinedBox, RouterLink } from "@/core/components"
import { ADV, AdvertisingContent } from "../../interfaces"
import { useAdvWriteAccess } from "../../hooks/useAdvWriteAccess"
import { buttonsCount, filesSummaryText } from "../../utils/contentsFormatting"
import { formatContentDateTime } from "../../utils/formatContentDateTime"
import { BindMaterialDialog } from "./bindMaterialDialog"
import { ContentViewerDialog } from "./contentViewerDialog"
import { CreateContentDialog } from "./createContentDialog"
import { DeleteContentDialog } from "./deleteContentDialog"

export const OrderContents = ({ order }: { order: ADV.Order }) => (
  <OrderContentsSession key={order.id} order={order} />
)

const OrderContentsSession = ({ order }: { order: ADV.Order }) => {
  const canWrite = useAdvWriteAccess()
  const [createOpen, setCreateOpen] = useState(false)
  const [bindOpen, setBindOpen] = useState(false)
  const [deleting, setDeleting] = useState<AdvertisingContent | null>(null)
  const [viewer, setViewer] = useState<{
    id: number
    source: "order" | "campaign"
  } | null>(null)
  const orderContents = order.contents ?? []
  const campaignContents = order.campaign_contents ?? []

  return (
    <Stack spacing={2} mt={2}>
      {(["order", "campaign"] as const).map((source) => {
        const isOrder = source === "order"
        const title = isOrder ? "Материалы ордера" : "Материалы кампании"
        const contents = isOrder ? orderContents : campaignContents
        return (
          <OutlinedBox key={source} component="section" aria-label={title}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h6" component="h2">
                  {title}
                </Typography>
                {isOrder && (
                  <RouterLink
                    title="Все материалы"
                    to={`/adv/contents?orders=${order.id}`}
                  />
                )}
              </Stack>
              {isOrder ? (
                canWrite && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button onClick={() => setCreateOpen(true)}>
                      Создать материал
                    </Button>
                    <Button onClick={() => setBindOpen(true)}>
                      Добавить существующий материал
                    </Button>
                  </Stack>
                )
              ) : (
                <Typography color="text.secondary">
                  Материалы кампании не означают, что ордер использовал именно
                  эти материалы
                </Typography>
              )}
              {contents.length === 0 ? (
                <Typography color="text.secondary">
                  Пока нет материалов
                </Typography>
              ) : (
                contents.map((content) => (
                  <Box key={content.id}>
                    <Typography
                      component="div"
                      sx={{
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        overflow: "hidden",
                      }}
                    >
                      {content.text || "Без текста"}
                    </Typography>
                    <Typography variant="body2">
                      {filesSummaryText(content)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Кнопки: {buttonsCount(content)} ·{" "}
                      {formatContentDateTime(content.created_at)}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        onClick={() => setViewer({ id: content.id, source })}
                      >
                        Просмотр
                      </Button>
                      {isOrder && canWrite && (
                        <Button
                          color="error"
                          onClick={() => setDeleting(content)}
                        >
                          Удалить
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </OutlinedBox>
        )
      })}
      {createOpen && canWrite && (
        <CreateContentDialog
          open
          presetOrderBinding={order.id}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {bindOpen && canWrite && (
        <BindMaterialDialog
          open
          orderId={order.id}
          onClose={() => setBindOpen(false)}
        />
      )}
      {viewer && (
        <ContentViewerDialog
          key={`${viewer.source}:${viewer.id}`}
          open
          contentId={viewer.id}
          readOnly={viewer.source === "campaign"}
          onClose={() => setViewer(null)}
        />
      )}
      {deleting && canWrite && (
        <DeleteContentDialog
          open
          content={deleting}
          onClose={() => setDeleting(null)}
        />
      )}
    </Stack>
  )
}
